import React, {useState} from "react";
import {Alert, Box, Button, CardHeader, Chip, CircularProgress, InputAdornment, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography} from "@mui/material";
import {PersonAdd, Refresh, Search} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {ContentContainer} from "../components/common/content-container";
import {ItemsSelect, SearchField, SearchFilterContainer} from "../components/common/search-filter-container";
import {useUser} from "../providers/user-context";
import AddUser from "../side-bar/add-user";
import useTenantUsers from "../user/use-tenant-users";
import UserManagementLayout from "../user/user-management-layout";

const roles = ["ADMIN", "OPERATOR", "COURIER"];
const columns = [["firstName", "FirstName"], ["lastName", "LastName"], ["email", "Email"], ["createdAt", "CreatedAt"]];

function TenantUsers() {
    const {t, i18n} = useTranslation();
    const {userRole} = useUser();
    const {query, content, totalElements, loading, error, changeFilter, setPage, refresh} = useTenantUsers();
    const [addOpen, setAddOpen] = useState(false);
    const [sortField, direction] = query.sort.split(",");
    const canCreate = ["SYSTEM", "ADMIN"].includes(userRole);
    const sort = field => changeFilter("sort", `${field},${sortField === field && direction === "asc" ? "desc" : "asc"}`);

    return <>
        <CardHeader subheader={t("TenantUsersDescription")}
                    action={<Button variant="outlined" startIcon={<Refresh/>} onClick={refresh} disabled={loading}>
                        {t("Refresh")}</Button>}/>
        <Box sx={{mb: 3}}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2,
                flexWrap: {xs: "wrap", md: "nowrap"}}}>
                <SearchFilterContainer sx={{flex: 1}}>
                    <SearchField label={t("Search")} placeholder={t("TenantUsersSearchHint")} value={query.search} size="small"
                                 InputProps={{startAdornment: <InputAdornment position="start"><Search/></InputAdornment>}}
                                 onChange={event => changeFilter("search", event.target.value)}/>
                    <ItemsSelect select label={t("Role")} value={query.role} size="small"
                                 InputLabelProps={{shrink: true}}
                                 SelectProps={{displayEmpty: true, renderValue: value => t(value || "AllRoles")}}
                                 onChange={event => changeFilter("role", event.target.value)}>
                        <MenuItem value="">{t("AllRoles")}</MenuItem>
                        {roles.map(role => <MenuItem key={role} value={role}>{t(role)}</MenuItem>)}
                    </ItemsSelect>
                    <ItemsSelect select label={t("Status")} value={query.active} size="small"
                                 InputLabelProps={{shrink: true}}
                                 SelectProps={{displayEmpty: true,
                                     renderValue: value => t(value === "true" ? "Active" : value === "false" ? "Inactive" : "AllStatuses")}}
                                 onChange={event => changeFilter("active", event.target.value)}>
                        <MenuItem value="">{t("AllStatuses")}</MenuItem>
                        <MenuItem value="true">{t("Active")}</MenuItem>
                        <MenuItem value="false">{t("Inactive")}</MenuItem>
                    </ItemsSelect>
                </SearchFilterContainer>
                {canCreate && <Button variant="contained" startIcon={<PersonAdd/>} onClick={() => setAddOpen(true)}
                                      sx={{flexShrink: 0, mt: 2}}>{t("AddUser")}</Button>}
            </Box>
        </Box>
        {error ? <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{t("Retry")}</Button>}>
            {t(error)}</Alert> : <ContentContainer aria-busy={loading}>
            {loading ? <Box sx={{p: 5, textAlign: "center"}} role="status">
                <CircularProgress aria-label={t("Loading")}/><Typography sx={{mt: 1}}>{t("Loading")}</Typography>
            </Box> : content.length === 0 ? <Typography role="status" sx={{p: 4, textAlign: "center"}}>
                {t("NoTenantUsersFound")}</Typography> : <TableContainer>
                <Table aria-label={t("UserManagement")}>
                    <TableHead><TableRow>
                        {columns.map(([field, label]) => <TableCell key={field}
                            sortDirection={sortField === field ? direction : false}>
                            <TableSortLabel active={sortField === field} direction={sortField === field ? direction : "asc"}
                                            onClick={() => sort(field)}>{t(label)}</TableSortLabel>
                        </TableCell>)}
                        <TableCell>{t("Role")}</TableCell><TableCell>{t("Status")}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{content.map(user => <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell><TableCell>{user.surname}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.createdDate ? new Date(user.createdDate).toLocaleDateString(i18n.language) : "—"}</TableCell>
                        <TableCell><Chip size="small" label={t(user.role)}/></TableCell>
                        <TableCell><Chip size="small" variant="outlined" color={user.active ? "success" : "default"}
                                         label={t(user.active ? "Active" : "Inactive")}/></TableCell>
                    </TableRow>)}</TableBody>
                </Table>
            </TableContainer>}
            <TablePagination component="div" count={totalElements} page={loading ? 0 : query.page}
                             rowsPerPage={query.size} rowsPerPageOptions={[10, 20, 50, 100]} disabled={loading}
                             onPageChange={(_, page) => setPage(page)} labelRowsPerPage={t("UsersPerPage")}
                             labelDisplayedRows={({from, to, count}) => t("UserPageRange", {from, to, count})}
                             getItemAriaLabel={type => t(type === "next" ? "NextUsersPage" : "PreviousUsersPage")}
                             onRowsPerPageChange={event => changeFilter("size", Number(event.target.value))}/>
        </ContentContainer>}
        {canCreate && <AddUser open={addOpen} onClose={() => setAddOpen(false)} onUserCreated={refresh}/>}
    </>;
}

export default function UserManagementPage(props) {
    return <UserManagementLayout {...props}><TenantUsers/></UserManagementLayout>;
}
